/**
 * Density — `110-design-system.md` §4. `compact` (the default) ⇄ `comfortable`.
 *
 * The <html> element carries `data-density`, and every control's height, padding and icon
 * size is a CSS custom property keyed off it (`styles/theme.css`). So the whole app
 * restyles from one attribute flip, and **no component ever reads this value** — they
 * consume the properties, never the setting. That is the entire design; a component that
 * branches on density has broken it.
 *
 * localStorage, exactly as `stores/theme.tsx` argues: it must be readable synchronously
 * before paint, and it is 9 bytes of per-device UI trivia holding no credentials. Mirroring
 * it to `PATCH /api/settings` (`100-settings.md`) is M7's job; localStorage stays the fast
 * path either way.
 */
import { useEffect, useSyncExternalStore } from 'react';

export type Density = 'compact' | 'comfortable';

const STORAGE_KEY = 'rn.density';

const listeners = new Set<() => void>();

function read(): Density {
  return localStorage.getItem(STORAGE_KEY) === 'comfortable' ? 'comfortable' : 'compact';
}

export function setDensity(density: Density): void {
  localStorage.setItem(STORAGE_KEY, density);
  document.documentElement.dataset.density = density;
  for (const listener of listeners) listener();
}

export function toggleDensity(): Density {
  const next: Density = read() === 'compact' ? 'comfortable' : 'compact';
  setDensity(next);
  return next;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useDensity(): Density {
  const density = useSyncExternalStore(subscribe, read, () => 'compact' as Density);

  // Stamps <html> on the first paint of a fresh tab, where nothing has called `setDensity`
  // yet. (`setDensity` writes the attribute itself, so this is a no-op thereafter.)
  useEffect(() => {
    document.documentElement.dataset.density = density;
  }, [density]);

  return density;
}
